import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthenticationService } from '../core/service/auth/authentication.service';
import { SKIP_AUTH } from './skip-auth-context';
import { SessionExpireComponent } from '../shared/session-expire/session-expire.component';
import { catchError, throwError } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LocalStorageService } from '../core/service/localStorage/local-storage.service';
import { LoaderService } from '../core/service/loader.service';
import { MailCacheService } from '../core/service/mailCache/mail-cache.service';
import { TmsSocketService } from '../core/service/tms-socket/tms-socket.service';
import { LockDialogService } from '../core/service/lock-dialog/lock-dialog.service';
import { isCourseFinderRequest } from './course-finder';

let isSessionExpireDialogOpen = false;

/**
 * Clears the session and sends the user to /login, run once the user
 * acknowledges the session-expire dialog.
 */
function clearSessionAndRedirect(
  localStorageService: LocalStorageService,
  mailCacheService: MailCacheService,
  tmsSocketService: TmsSocketService,
  router: Router,
  dialog: MatDialog,
): void {
  localStorageService.removeIndexedDbKey('currentUser');
  localStorageService.clearIndexedDb();
  localStorageService.removeKey('access_token');
  localStorageService.removeKey('refreshToken');

  localStorageService.removeIndexedDbKey('switchUser');
  localStorageService.removeKey('switchUserToken');
  localStorageService.removeKey('isSwitchUser');
  mailCacheService.clearAllCache();
  tmsSocketService.disconnect(); // clear TMS lock state so awayLockDeactivateGuard doesn't block the redirect to /login

  dialog.closeAll();
  router.navigate(['/login']);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const skipAuth = req.context.get(SKIP_AUTH);
  if (skipAuth) return next(req); //  Skip adding token
  const dialog = inject(MatDialog);
  const loader = inject(LoaderService);
  const router = inject(Router); //  FIX: Inject Router
  const localStorageService = inject(LocalStorageService);
  const authService = inject(AuthenticationService);
  const mailCacheService = inject(MailCacheService);
  const tmsSocketService = inject(TmsSocketService);
  const lockDialogService = inject(LockDialogService);
  
  const isSwitchUser = JSON.parse(
    localStorageService.getKey('isSwitchUser') || 'false'
  );
  // ✅ Only check isSwitchUser for GET requests
  let token: string | null = null;
  // let isSessionExpireDialogOpen = false;

  if (req.method === 'GET') {
    const isSwitchUser = JSON.parse(
      localStorageService.getKey('isSwitchUser') || 'false'
    );
    token = isSwitchUser
      ? authService.getSwitchUserToken()
      : authService.getToken();
  } else {
    token = authService.getToken();
  }

  const cloned = token
    ? req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    })
    : req;

  return next(cloned).pipe(
    catchError((error) => {
      /**
       * A 401 from COURSE-FINDER is not this console's session expiring.
       *
       * course-finder-be is a separate service with its own token rules. It
       * answers 401 when no token is attached, and it answers 401 for a token
       * that is perfectly valid but not accepted on that route. Neither says
       * anything about the console's session.
       *
       * Handled below, both would open the session-expire dialog and then
       * `clearSessionAndRedirect` - wiping localStorage and IndexedDB and
       * bouncing to /login while the user was in the middle of a course search.
       * Reproduced in a browser on the sibling portal: with enforcement on and
       * no token attached, opening Search Program redirected straight to the
       * login screen.
       *
       * So course-finder errors are rethrown untouched and the feature shows its
       * own message. The token is still attached on the way out, by the same
       * interceptor a few lines above - that part is already correct, because
       * course-finder-be verifies Crizac's own JWTAPIKEY / AGENTJWTAPIKEY.
       */
      if (isCourseFinderRequest(req.url)) return throwError(() => error);

      if (
        error.status === 401 &&
        error?.error?.message?.includes('jwt expired') &&
        !isSessionExpireDialogOpen || error.status === 401 && !isSessionExpireDialogOpen
      ) {
        isSessionExpireDialogOpen = true;
        loader.hide();

        // Store the current URL for redirect after login (setRedirectUrl filters
        // out interstitial pages like /start-day, /sign-off-wrapup, /away-lock)
        const currentUrl = router.url;
        authService.setRedirectUrl(currentUrl);

        // The away-lock screen (and any dialog opened on top of it via
        // LockDialogService, e.g. the Logout confirmation) renders above the
        // app-wide overlay container (see styles.scss) so pre-existing popups
        // stay hidden underneath until unlock — but that also buries this
        // dialog, making it visible in the DOM yet unreachable. Drop both so
        // the session-expire dialog actually reaches the user.
        if (tmsSocketService.isLocked) {
          tmsSocketService.hideLockOverlay();
          lockDialogService.closeAll();
        }

        const dialogRef = dialog.open(SessionExpireComponent, {
          disableClose: true,
          width: '400px',
        });

        dialogRef.afterClosed().subscribe(() => {
          isSessionExpireDialogOpen = false;
          clearSessionAndRedirect(localStorageService, mailCacheService, tmsSocketService, router, dialog);
        });
      }

      return throwError(() => {
        console.log('throw Aer');

        return error;
      });
    })
  );
};
